import React, { useState, useEffect } from 'react';
import { useGetDashboardCategoriesQuery, useGetDashboardProductsQuery, useGetDashboardWarehousesQuery } from 'Services/Admin/DashboardApiSlice';
import { FiChevronDown } from 'react-icons/fi';
import { Filter, Calendar, X, RefreshCw, CheckCircle } from 'lucide-react';

// Custom Scrollbar CSS to make it "lite" and mobile-like
const scrollbarStyles = `
  .custom-scrollbar::-webkit-scrollbar {
    width: 4px;
    height: 4px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: transparent;
    border-radius: 10px;
    transition: background 0.3s ease;
  }
  .custom-scrollbar:hover::-webkit-scrollbar-thumb {
    background: #E2E8F0;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #CBD5E1;
  }
  /* Firefox */
  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: transparent transparent;
  }
  .custom-scrollbar:hover {
    scrollbar-color: #E2E8F0 transparent;
  }
`;

// Reusable pill for other filters with custom scrollable dropdown
const FilterPill = ({ icon, label, defaultText, options, hideLabel = false, value, onChange, showResetOption = true, resetOptionLabel }: any) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // Close on outside click
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options?.filter((opt: any) => {
    const displayVal = typeof opt === 'string' ? opt : (opt?.categoryName || opt?.productName || opt?.warehouseName || opt?.name || opt?.itemDesc || opt?.itemName || opt?.title || '');
    return displayVal.toLowerCase().includes(searchTerm.toLowerCase());
  }) || [];

  const selectedDisplay = React.useMemo(() => {
    if (!value) return defaultText;
    const found = options?.find((opt: any) => {
      const val = typeof opt === 'string' ? opt : (opt?.categoryId || opt?.productId || opt?.warehouseId || opt?.id || '');
      return String(val) === String(value);
    });
    if (typeof found === 'string') return found;
    return found?.categoryName || found?.productName || found?.warehouseName || found?.name || found?.itemDesc || found?.itemName || found?.title || defaultText;
  }, [value, options, defaultText]);

  return (
    <>
      <style>{scrollbarStyles}</style>
      <div 
        ref={dropdownRef}
        className="relative w-full h-full"
      >
        <div 
          onClick={() => setIsOpen(!isOpen)}
          className={`flex items-center bg-white border ${isOpen ? 'border-blue-400 ring-2 ring-blue-50' : 'border-gray-200'} rounded-lg px-3 py-1.5 shadow-sm text-sm hover:border-gray-300 transition-all cursor-pointer min-w-0 h-full`}
          title={selectedDisplay}
        >
          <span className="flex-shrink-0 mr-2 flex items-center justify-center text-lg">{icon}</span>
          {!hideLabel && <span className="text-gray-400 mr-1 whitespace-nowrap flex-shrink-0">{label}</span>}
          <div className="text-[#1B2559] font-bold truncate pr-6 relative flex-1 min-w-0 text-[11px] sm:text-xs">
            {selectedDisplay}
            <span className="absolute right-0 top-1/2 -translate-y-1/2 text-gray-400 text-[10px]">
              <FiChevronDown />
            </span>
          </div>
        </div>

        {isOpen && (
          <div className="absolute top-full left-0 mt-2 w-max min-w-full max-w-[300px] sm:max-w-[400px] bg-white border border-gray-100 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.15)] rounded-xl z-[999] overflow-hidden animate-in fade-in slide-in-from-top-1 duration-200">
            <div className="p-2 border-b border-gray-50 bg-gray-50/30">
              <input 
                type="text"
                autoFocus
                placeholder="Search..."
                className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 bg-white shadow-inner transition-all box-border"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="max-h-[300px] overflow-y-auto custom-scrollbar flex flex-col py-1">
              {showResetOption && (
                <div 
                  onClick={() => { onChange(''); setIsOpen(false); }}
                  className={`px-4 py-2.5 text-xs cursor-pointer transition-colors flex-shrink-0 ${!value ? 'bg-blue-50 text-blue-600 font-bold' : 'text-gray-600 hover:bg-gray-50 font-semibold'}`}
                >
                  {resetOptionLabel || defaultText}
                </div>
              )}
              {filteredOptions.length === 0 ? (
                <div className="px-4 py-8 text-center text-gray-400 text-xs italic">No results found</div>
              ) : (
                filteredOptions.map((opt: any, index: number) => {
                  const displayVal = typeof opt === 'string' ? opt : (opt?.categoryName || opt?.productName || opt?.warehouseName || opt?.name || opt?.itemDesc || opt?.itemName || opt?.title || '');
                  const valueVal = typeof opt === 'string' ? opt : (opt?.categoryId || opt?.productId || opt?.warehouseId || opt?.id || displayVal);
                  const isSelected = String(valueVal) === String(value);

                  return (
                    <div 
                      key={index}
                      onClick={() => {
                        onChange(valueVal);
                        setIsOpen(false);
                        setSearchTerm('');
                      }}
                      className={`px-4 py-2.5 text-xs cursor-pointer transition-colors border-l-2 whitespace-normal break-words flex-shrink-0 ${
                        isSelected 
                          ? 'bg-blue-50 border-blue-500 text-blue-700 font-bold' 
                          : 'border-transparent text-gray-700 font-semibold hover:bg-gray-50 hover:border-gray-200'
                      }`}
                    >
                      {displayVal}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export const DashboardFilters = ({ 
  globalDate, 
  onDateChange, 
  selectedWarehouse, 
  onWarehouseChange,
  selectedChannel,
  onChannelChange,
  selectedCategory,
  onCategoryChange,
  selectedProduct,
  onProductChange
}: any) => {
  const plant = process.env.REACT_APP_PLANT || '';
  
  // Fetch newly integrated endpoints
  const { data: categoryData } = useGetDashboardCategoriesQuery({ plant });
  const { data: productsData } = useGetDashboardProductsQuery({ plant });
  const { data: warehouseData } = useGetDashboardWarehousesQuery({ plant });

  const categoriesList = Array.isArray(categoryData) ? categoryData : (categoryData?.results || categoryData?.data || []);
  const productsList = Array.isArray(productsData) ? productsData : (productsData?.results || productsData?.data || []);
  const warehouseList = Array.isArray(warehouseData) ? warehouseData : (warehouseData?.results || warehouseData?.data || []);
  const todayStr = new Date().toISOString().split('T')[0];

  const ranges = [
    "Today", "Yesterday", "Last 7 Days", "This Week", "Last 15 Days", 
    "Last 30 Days", "This Month", "Last Month", "This Quarter", 
    "Last Quarter", "This Year", "Last Year", "Custom Range"
  ];

  const toInputDate = (str: string) => {
    if (!str || !str.includes('/')) return str || '';
    const [d, m, y] = str.split('/');
    return `${y}-${m}-${d}`;
  };

  // --- Local Buffering State ---
  const [localRange, setLocalRange] = useState(globalDate.range);
  const [localCustomFrom, setLocalCustomFrom] = useState(toInputDate(globalDate.from));
  const [localCustomTo, setLocalCustomTo] = useState(toInputDate(globalDate.to));
  const [localWarehouse, setLocalWarehouse] = useState(selectedWarehouse);
  const [localCategory, setLocalCategory] = useState(selectedCategory);
  const [localProduct, setLocalProduct] = useState(selectedProduct);

  // Sync with external state (e.g. on mount or if changed externally)
  useEffect(() => {
    setLocalRange(globalDate.range);
    setLocalCustomFrom(toInputDate(globalDate.from));
    setLocalCustomTo(toInputDate(globalDate.to));
  }, [globalDate]);

  useEffect(() => { setLocalWarehouse(selectedWarehouse); }, [selectedWarehouse]);
  useEffect(() => { setLocalCategory(selectedCategory); }, [selectedCategory]);
  useEffect(() => { setLocalProduct(selectedProduct); }, [selectedProduct]);

  const handleReset = () => {
    onDateChange("Today");
    onWarehouseChange("");
    onCategoryChange("");
    onProductChange("");
  };

  // Helper to instantly clear one filter
  const handleClearChip = (type: string) => {
    if (type === 'date') onDateChange("Today");
    if (type === 'warehouse') onWarehouseChange("");
    if (type === 'category') onCategoryChange("");
    if (type === 'product') onProductChange("");
  };

  // Active filter computation (based on applied state, not local state)
  const activeFiltersCount = 
    (globalDate.range !== "Today" ? 1 : 0) +
    (selectedWarehouse ? 1 : 0) +
    (selectedCategory ? 1 : 0) +
    (selectedProduct ? 1 : 0);

  return (
    // Removed overflow-hidden so that dropdown menus can freely float above everything else
    <div className="bg-white border border-gray-100 rounded-2xl shadow-sm mb-6 flex flex-col w-full relative z-50">
      
      {/* Header */}
      <div className="flex items-center gap-2 p-4 border-b border-gray-50 bg-gray-50/30 rounded-t-2xl">
        <Filter className="w-4 h-4 text-blue-500" />
        <span className="text-sm font-black text-[#1B2559] tracking-normal">Dashboard Control Panel</span>
        {activeFiltersCount > 0 && (
          <span className="bg-blue-100 text-blue-600 text-[10px] font-bold px-2 py-0.5 rounded-full ml-1">
            {activeFiltersCount}
          </span>
        )}
      </div>

      <div className="p-5 flex flex-col gap-5">
        {/* Dropdowns Row - Improved width allocation and responsiveness */}
        <div className="flex flex-col sm:flex-row flex-wrap lg:flex-nowrap items-end gap-4 relative z-50">
          
          <div className="flex flex-col gap-1.5 min-w-0 w-full sm:w-[calc(50%-8px)] lg:w-[20%] relative z-[50]">
            <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">Date Range</label>
            <div className="h-[40px] w-full">
              <FilterPill 
                icon={<span className="text-blue-400 opacity-70">⏱️</span>}
                defaultText="Today"
                options={ranges}
                hideLabel={true}
                value={localRange === "Today" ? "" : localRange}
                onChange={(val: string) => {
                  const newRange = val || "Today";
                  setLocalRange(newRange);
                  onDateChange(newRange, localCustomFrom, localCustomTo);
                }}
                showResetOption={false}
              />
            </div>
          </div>

          {/* Warehouse - 25% */}
          <div className="flex flex-col gap-1.5 min-w-0 h-[40px] w-full sm:w-[calc(50%-8px)] lg:w-[25%] relative z-[49]">
            <FilterPill 
              icon={<span className="text-orange-400">🏢</span>} 
              defaultText="All Warehouses" 
              options={warehouseList}
              hideLabel={true}
              value={localWarehouse}
              onChange={(val: string) => {
                setLocalWarehouse(val);
                onWarehouseChange(val);
              }}
              showResetOption={true}
              resetOptionLabel="All Warehouses"
            />
          </div>
          
          {/* Category - 25% */}
          <div className="flex flex-col gap-1.5 min-w-0 h-[40px] w-full sm:w-[calc(50%-8px)] lg:w-[25%] relative z-[48]">
            <FilterPill 
              icon={<span className="text-rose-400">📦</span>} 
              defaultText="All Categories" 
              options={categoriesList} 
              hideLabel={true}
              value={localCategory}
              onChange={(val: string) => {
                setLocalCategory(val);
                onCategoryChange(val);
              }}
              showResetOption={true}
              resetOptionLabel="All Categories"
            />
          </div>

          {/* Product - 30% */}
          <div className="flex flex-col gap-1.5 min-w-0 h-[40px] w-full sm:w-[calc(50%-8px)] lg:w-[30%] relative z-[47]">
            <FilterPill 
              icon={<span className="text-teal-500">📋</span>} 
              defaultText="All Products" 
              options={productsList} 
              hideLabel={true} 
              value={localProduct}
              onChange={(val: string) => {
                setLocalProduct(val);
                onProductChange(val);
              }}
              showResetOption={true}
              resetOptionLabel="All Products"
            />
          </div>

        </div>

        {/* Custom Range Expansion */}
        {localRange === "Custom Range" && (
          <div className="flex flex-wrap items-center gap-4 bg-gray-50/50 p-4 rounded-xl border border-dashed border-gray-200 mt-1">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">From:</span>
              <input 
                type="date" 
                max={todayStr}
                value={localCustomFrom}
                onChange={(e) => {
                  setLocalCustomFrom(e.target.value);
                  onDateChange(localRange, e.target.value, localCustomTo);
                }}
                className="bg-white border border-gray-200 text-[#1B2559] text-xs font-bold rounded-lg px-3 py-1.5 outline-none cursor-pointer focus:border-blue-400 focus:ring-2 focus:ring-blue-50 transition-all"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">To:</span>
              <input 
                type="date" 
                min={localCustomFrom}
                max={todayStr}
                value={localCustomTo}
                onChange={(e) => {
                  setLocalCustomTo(e.target.value);
                  onDateChange(localRange, localCustomFrom, e.target.value);
                }}
                className="bg-white border border-gray-200 text-[#1B2559] text-xs font-bold rounded-lg px-3 py-1.5 outline-none cursor-pointer focus:border-blue-400 focus:ring-2 focus:ring-blue-50 transition-all"
              />
            </div>
          </div>
        )}

        {/* Actions & Chips Row */}
        <div className="flex flex-col md:flex-row md:items-center justify-between border-t border-gray-50 pt-5 gap-4">
          
          {/* Active Filter Chips (Reading from Appplied Props) */}
          <div className="flex items-center gap-2 flex-wrap w-full md:w-auto flex-1 min-w-0">
            <span className="text-[11px] font-bold text-[#1B2559] mr-1">Applied Filters:</span>
            
            {globalDate.range !== "Today" && (
              <div className="flex items-center gap-1.5 bg-blue-50/50 border border-blue-100 text-blue-700 px-2.5 py-1.5 rounded-lg text-[11px] font-bold transition-all hover:bg-blue-100 whitespace-nowrap">
                <Calendar className="w-3.5 h-3.5 text-blue-500" />
                {globalDate.range}
                <button onClick={() => handleClearChip('date')} className="hover:bg-blue-200 rounded p-0.5 ml-1 transition-colors"><X className="w-3.5 h-3.5 text-blue-500" /></button>
              </div>
            )}
            
            {selectedWarehouse !== "" && (
              <div className="flex items-center gap-1.5 bg-orange-50/50 border border-orange-100 text-orange-700 px-2.5 py-1.5 rounded-lg text-[11px] font-bold transition-all hover:bg-orange-100 max-w-full">
                <span className="text-orange-500 shrink-0">🏢</span>
                <span className="truncate max-w-[150px] sm:max-w-[200px]">
                  {warehouseList.find((w: any) => w.warehouseId === selectedWarehouse)?.warehouseName || selectedWarehouse}
                </span>
                <button onClick={() => handleClearChip('warehouse')} className="hover:bg-orange-200 rounded p-0.5 ml-1 transition-colors shrink-0"><X className="w-3.5 h-3.5 text-orange-500" /></button>
              </div>
            )}

            {selectedCategory !== "" && (
              <div className="flex items-center gap-1.5 bg-rose-50/50 border border-rose-100 text-rose-700 px-2.5 py-1.5 rounded-lg text-[11px] font-bold transition-all hover:bg-rose-100 max-w-full">
                <span className="text-rose-500 shrink-0">📦</span>
                <span className="truncate max-w-[150px] sm:max-w-[200px]">
                  {categoriesList.find((c: any) => c.categoryId === selectedCategory)?.categoryName || selectedCategory}
                </span>
                <button onClick={() => handleClearChip('category')} className="hover:bg-rose-200 rounded p-0.5 ml-1 transition-colors shrink-0"><X className="w-3.5 h-3.5 text-rose-500" /></button>
              </div>
            )}

            {selectedProduct !== "" && (
              <div className="flex items-center gap-1.5 bg-teal-50/50 border border-teal-100 text-teal-700 px-2.5 py-1.5 rounded-lg text-[11px] font-bold transition-all hover:bg-teal-100 max-w-full">
                <span className="text-teal-500 shrink-0">📋</span>
                <span className="truncate max-w-[150px] sm:max-w-[200px]">
                  {productsList.find((p: any) => p.productId === selectedProduct)?.productName || selectedProduct}
                </span>
                <button onClick={() => handleClearChip('product')} className="hover:bg-teal-200 rounded p-0.5 ml-1 transition-colors shrink-0"><X className="w-3.5 h-3.5 text-teal-500" /></button>
              </div>
            )}

            {activeFiltersCount > 0 && (
              <button 
                onClick={handleReset}
                className="text-[11px] font-bold text-[#2563EB] hover:text-blue-800 ml-2 transition-colors"
              >
                Clear all
              </button>
            )}

            {activeFiltersCount === 0 && (
              <span className="text-[11px] text-gray-400 italic">None</span>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};
